import { ReactNode } from 'react';
import type { DocumentSignPublicBranding } from '@/lib/api';
import { onColor } from '@/lib/brand-color';

interface BrandedSigningFrameProps {
  branding: DocumentSignPublicBranding | null | undefined;
  /** Falls back to the envelope title line under the wordmark. */
  subtitle?: ReactNode;
  right?: ReactNode;
  /**
   * Full-height by default, because this is the whole page. The branding
   * settings preview renders the same component inline and turns it off.
   */
  fullHeight?: boolean;
  children: ReactNode;
}

/**
 * Applies a sender's branding to the signing surfaces.
 *
 * Every value here arrives from the server already validated — colours match
 * `#rrggbb`, the logo is a PNG/SVG data URL, text is plain. That matters
 * because this page is served on the PLATFORM ORIGIN, outside any iframe
 * sandbox, to recipients who may be signed in to Fias. Interpolating
 * sender-supplied CSS or markup here would be a cross-site-scripting vector
 * against other customers' sessions, which is why branding is a closed set of
 * tokens rather than an uploaded stylesheet.
 *
 * The logo is rendered through an `<img>` deliberately: an SVG in an image tag
 * cannot execute script or reach the parent document, which inlining it as
 * markup would allow.
 *
 * Unbranded senders get the Fias default, so this is safe to wrap
 * unconditionally.
 *
 * # The Fias attribution is not optional
 *
 * Whatever the sender's branding says, the footer names Fias. A branded
 * signing page sits on a fias.io origin and asks for a legally binding
 * signature, so a recipient must be able to establish who is actually hosting
 * it without trusting the header — the header is the part the sender controls.
 * It is also the control that still holds if an entitlement is ever granted to
 * the wrong workspace: branding can be wrong, but it can never be anonymous.
 *
 * So there is no token, prop or profile field that removes it. If a partner
 * asks, the answer is no, and the reason is above.
 */
export function BrandedSigningFrame({
  branding,
  subtitle,
  right,
  fullHeight = true,
  children,
}: BrandedSigningFrameProps) {
  const name = branding?.displayName?.trim() || 'Document Sign';
  const primary = branding?.primaryColor ?? undefined;
  const accent = branding?.accentColor ?? undefined;

  // The brand colour is rendered as a BAND with computed text on it, rather
  // than as coloured text on the page background.
  //
  // That is not a style preference. A sender's colour has to be legible in
  // both themes, and the platform cannot know theirs in advance — a dark
  // primary reads correctly as heading text in light mode and nearly
  // disappears in dark mode. Putting the colour behind the text instead makes
  // the pairing self-contained: the foreground is derived from the background,
  // so it is legible regardless of the theme around it. It is also what a
  // white-labelled product is expected to look like.
  // The sender's chosen foreground when they set one, otherwise the colour of
  // maximum contrast. The override is validated against the band before it is
  // stored, so anything arriving here is already legible on it.
  const headerText = branding?.headerTextColor ?? (primary ? onColor(primary) : undefined);

  // Still exposed as custom properties so nested content can opt in without
  // the value being threaded through every component.
  const themeVars = {
    ...(primary ? { ['--ds-brand-primary' as string]: primary } : {}),
    ...(accent ? { ['--ds-brand-accent' as string]: accent } : {}),
  } as React.CSSProperties;

  return (
    <div
      className={`bg-background text-foreground ${fullHeight ? 'min-h-screen' : ''} ${
        fullHeight ? 'flex flex-col' : ''
      }`}
      style={themeVars}
    >
      <div
        className={primary ? '' : 'border-b'}
        style={primary ? { backgroundColor: primary, color: headerText } : undefined}
      >
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {branding?.logoDataUrl && (
              <img
                src={branding.logoDataUrl}
                alt=""
                aria-hidden="true"
                className="h-8 w-8 shrink-0 object-contain"
              />
            )}
            <div className="min-w-0">
              <h1 className="text-base font-semibold truncate">{name}</h1>
              {/* Inherits the computed header colour. The muted class the
                  unbranded header uses would be invisible on a brand band. */}
              {/*
                The FRAME owns this colour, and callers must not set one.

                A child's own `text-muted-foreground` beats the colour
                inherited from the band, so a caller styling its subtitle
                "muted" renders theme grey on the brand colour no matter what
                the sender chose — which is exactly what an explicit white
                header setting appeared not to apply to. Muted is applied here
                and ONLY when there is no band; on a band, the sender's colour
                carries, and hierarchy comes from size and weight.
              */}
              <div className={primary ? undefined : 'text-muted-foreground'}>{subtitle}</div>
            </div>
          </div>
          <div className={`shrink-0 ${primary ? '' : 'text-muted-foreground'}`}>{right}</div>
        </div>
      </div>
      <div className={fullHeight ? 'flex-1' : undefined}>{children}</div>
      <SigningPlatformAttribution />
    </div>
  );
}

/**
 * "Secured by Fias" — shown on every signing surface, branded or not.
 *
 * Deliberately plain and deliberately unconditional. It takes no props, reads
 * no branding and has no way to be turned off, so there is no code path that
 * renders a signing page with no indication of who hosts it. Anything that
 * made it configurable would make it worthless: the senders you would most
 * want it against are exactly the ones who would switch it off.
 *
 * It sits below the content rather than in the header so it does not compete
 * with a partner's mark — the point is attribution a recipient can find, not a
 * Fias banner on someone else's brand.
 */
function SigningPlatformAttribution() {
  return (
    <div className="border-t mt-8">
      <div className="max-w-5xl mx-auto px-4 py-4 text-xs text-muted-foreground">
        Secured by Fias · This document is being signed electronically through the Fias platform.
      </div>
    </div>
  );
}

/**
 * Sender's closing message on the post-signature screen.
 *
 * Rendered as text with a single optional call to action. The link is
 * https-validated server-side; `rel="noopener noreferrer"` is belt-and-braces
 * for a destination the platform does not control.
 */
export function BrandedCompletionMessage({
  branding,
}: {
  branding: DocumentSignPublicBranding | null | undefined;
}) {
  const message = branding?.completionMessage?.trim();
  const ctaLabel = branding?.completionCtaLabel?.trim();
  const ctaUrl = branding?.completionCtaUrl?.trim();
  if (!message && !(ctaLabel && ctaUrl)) return null;

  // PRIMARY first, accent only as a fallback.
  //
  // The accent is the lighter of a brand's two colours far more often than not,
  // which made the one control on the completion screen the quietest thing on
  // it. The primary is the colour the sender already chose to carry their
  // header, so a button in it reads as theirs and as the next step.
  const ctaBackground = branding?.primaryColor ?? branding?.accentColor ?? undefined;

  return (
    <div className="mt-6 pt-6 border-t space-y-4 text-center">
      {message && <p className="text-sm whitespace-pre-wrap">{message}</p>}
      {ctaLabel && ctaUrl && (
        // Centred UNDER the message rather than trailing it on the left: this
        // is the only action on the screen, and the screen is otherwise a
        // confirmation the signer has finished reading.
        <div className="flex justify-center">
          <a
            href={ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center rounded-md px-4 py-2 text-sm font-medium"
            // Foreground derived from the sender's own colour. Hard-coding white
            // here was unreadable on any light accent — which the Service
            // Ventures palette is.
            style={
              ctaBackground
                ? { backgroundColor: ctaBackground, color: onColor(ctaBackground) }
                : undefined
            }
          >
            {ctaLabel}
          </a>
        </div>
      )}
    </div>
  );
}
